import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import Headline from '../typography/headline'
import Paragraph from '../typography/paragraph'
import {
  findServiceData,
  getMeetingTypeContent,
} from '../../containers/bada/content'
import { CUSTOMER_FORM } from '../../containers/bada/reducer/Wizard'
import settings from '../settings'
import CalendarHOC2 from '../calendar2'
import { useCms } from '../cms/CmsContext'
import { Select } from '../../containers/appointment-details/meeting-editor/Select'
import { useServiceProviders } from '../../hooks/service-provider/useServiceProviders'
import { isDesigner } from '../../../utils/initValues'
import { SectionItemHeading } from '../../containers/appointment-details/sharedUI'

const StyledHeadlineDesktop = styled(Headline)`
  display: none;
  margin-bottom: 22px;
  ${(props) => props.theme.media.min.sm`
    display: block;
  `}
  text-transform:inherit;
  text-transform: ${theme('slots.headers.textTransform')};
  line-height: ${theme('slots.headers.lineHeight')};
`

const StyledHeadlineMobile = styled(Headline)`
  display: block;
  margin-bottom: 22px;
  ${(props) => props.theme.media.min.sm`
    display: none;
  `}
  text-transform:inherit;
  text-transform: ${theme('slots.headers.textTransform')};
`

const StyledParagraphDesktop = styled(Paragraph)`
  display: none;
  font-size: 16px;
  ${(props) => props.theme.media.min.sm`
    display: block;
  `}
`

const StyledParagraphMobile = styled(Paragraph)`
  display: block;
  font-size: 16px;
  ${(props) => props.theme.media.min.sm`
    display: none;
  `}
`

const StyledNoteDesktop = styled(Paragraph)`
  display: none;
  ${(props) => props.theme.media.min.sm`
    display: block;
  `}
  font-size: 16px;
  background-color: #ebd58b;
  padding: 16px;
  border-radius: 4px;
  > strong {
    font-weight: 700;
  }
`

const StyledNoteMobile = styled(Paragraph)`
  display: block;
  ${(props) => props.theme.media.min.sm`
    display: none;
  `}
  font-size: 16px;
  background-color: #ebd58b;
  padding: 16px;
  border-radius: 4px;
  > strong {
    font-weight: 700;
  }
`

const CalenderContainer = styled(CalendarHOC2)`
  position: relative;
`

const DesignerContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`

const DesignerItem = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 300px;
`

const SlotSelection = ({
  selectedStore,
  selectedService,
  onChange,
  onMount,
  selectedServiceProvider,
  flow,
  updateUiState,
}) => {
  const [selectedDuration, setSelectedDuration] = useState(
    selectedService?.duration || 60
  )

  useEffect(() => {
    setSelectedDuration(selectedService?.duration || 60)
  }, [selectedService])

  const { services } = useCms()
  useEffect(() => {
    onMount()
  }, [])

  const { data: providers } = useServiceProviders(
    selectedStore?.id,
    selectedService?.serviceId,
    isDesigner,
    !isDesigner
  )

  const serviceData = useMemo(
    () => findServiceData(services, selectedService),
    [services, selectedService]
  )

  const noSlotMeetingType = useMemo(
    () =>
      getMeetingTypeContent({
        meetingTypes: services,
        currentMeetingTypeName: selectedService?.id,
        bookingSystem: CUSTOMER_FORM,
        subSystem: selectedStore?.simplyBookMeSubsystem,
        serviceId: selectedService?.serviceId,
      }),
    [selectedService, selectedStore]
  )
  const updateSelectedServiceProvider = useCallback(
    (e) => {
      const provider = providers.find((p) => `${p.id}` === e.target.value)
      if (!provider) {
        updateUiState({ selectedServiceProvider: null })
      } else {
        updateUiState({ selectedServiceProvider: provider })
      }
    },
    [updateUiState, providers]
  )
  const updateSelectedDuration = useCallback((e) => {
    setSelectedDuration(parseInt(e.target.value, 10))
  }, [])

  const durationOptions = useMemo(() => {
    const options = []
    const timeframe = settings.timeframe || 15
    let temp = timeframe
    while (temp <= 180) {
      options.push({ value: temp, label: `${temp} mins` })
      temp += timeframe
    }
    // Ensure selectedService duration is in the list if it's not a multiple of timeframe
    if (
      selectedService?.duration &&
      !options.find((o) => o.value === selectedService.duration)
    ) {
      options.push({
        value: selectedService.duration,
        label: `${selectedService.duration} mins`,
      })
      options.sort((a, b) => a.value - b.value)
    }
    return options
  }, [selectedService])

  if (
    !selectedService ||
    !selectedStore ||
    selectedStore.bookingSystem === CUSTOMER_FORM
  )
    return null

  if (!serviceData) return null

  return (
    <>
      {serviceData.slotSelectionHeadingDesktop && (
        <StyledHeadlineDesktop>
          {serviceData.slotSelectionHeadingDesktop}
        </StyledHeadlineDesktop>
      )}
      {serviceData.slotSelectionHeadingMobile && (
        <StyledHeadlineMobile>
          {serviceData.slotSelectionHeadingMobile}
        </StyledHeadlineMobile>
      )}
      {serviceData.slotSelectionTextDesktop && (
        <StyledParagraphDesktop
          dangerouslySetInnerHTML={{
            __html: serviceData.slotSelectionTextDesktop,
          }}
        />
      )}
      {serviceData.slotSelectionTextMobile && (
        <StyledParagraphMobile
          dangerouslySetInnerHTML={{
            __html: serviceData.slotSelectionTextMobile,
          }}
        />
      )}
      {serviceData.slotSelectionNoteDesktop && (
        <StyledNoteDesktop
          dangerouslySetInnerHTML={{
            __html: serviceData.slotSelectionNoteDesktop,
          }}
        />
      )}
      {serviceData.slotSelectionNoteMobile && (
        <StyledNoteMobile
          dangerouslySetInnerHTML={{
            __html: serviceData.slotSelectionNoteMobile,
          }}
        />
      )}
      {isDesigner && providers?.length > 1 && (
        <DesignerContainer>
          <DesignerItem>
            <SectionItemHeading>Designer</SectionItemHeading>
            <Select
              options={[
                { value: '-1000', label: 'Any' },
                ...providers.map((d) => ({
                  value: d.id,
                  label: [d.name, d.hidden && 'Hidden', d.virtual && 'Virtual']
                    .filter(Boolean)
                    .join(' - '),
                })),
              ]}
              onChange={updateSelectedServiceProvider}
              value={
                providers.find((d) => d.id === selectedServiceProvider?.id)?.id
              }
            />
          </DesignerItem>
          <DesignerItem>
            <SectionItemHeading>Duration</SectionItemHeading>
            <Select
              options={durationOptions}
              onChange={updateSelectedDuration}
              value={selectedDuration}
            />
          </DesignerItem>
        </DesignerContainer>
      )}
      <CalenderContainer
        onChange={onChange}
        storeId={selectedStore.id}
        locationId={selectedStore.simplyBookMeId.toString()}
        meetingTypeId={selectedService.serviceId.toString()}
        sbmInstance={selectedStore.simplyBookMeSubsystem}
        serviceData={serviceData}
        noSlotMeetingType={noSlotMeetingType}
        selectedServiceProvider={selectedServiceProvider}
        flow={flow}
        updateUiState={updateUiState}
        serviceDuration={selectedDuration}
      />
    </>
  )
}

export default SlotSelection
